<template>
  <div class="w-full group">
    <label
      :for="name"
      class="flex items-center gap-2 mb-1 pl-4 text-sm font-medium dark:text-gray-300 text-primary h-min-5"
    >
      {{ $t(label || "")
      }}<span v-if="required" class="text-danger dark:text-danger"> *</span>
      <tippy v-if="tooltip" class="flex items-center justify-center">
        <BaseIcon
          name="info"
          class="text-gray-400 group-hover:text-success hover:ring-success text-[18px]"
        />
        <template #content>
          <slot name="tooltip">{{ tooltip }}</slot>
        </template>
      </tippy>
    </label>
    <Multiselect
      :value="inputValue"
      :options="options"
      :searchable="true"
      :placeholder="$t(placeholder || 'start typing...')"
      label="name"
      trackBy="name"
      :name="name"
      :classes="classess"
      :class="[
        errorMessage ? 'border-error/30' : 'border-gray-300',
        disabled ? 'bg-gray-200' : 'bg-gray-50 dark:border-gray-600',
      ]"
      @update:modelValue="onChange"
      :disabled="disabled"
    >
      <template v-slot:singlelabel="{ value }">
        <div class="multiselect-single-label">
          <template v-if="!translate">
            {{ $t(value?.name || "") }}
          </template>
          <template v-else>
            {{ value?.name || value?.value }}
          </template>
        </div>
      </template>
      <template v-slot:option="{ option }">
        <template v-if="!translate">
          {{ $t(option?.name || "") }}
        </template>
        <template v-else>
          {{ option?.name }}
        </template>
      </template>
    </Multiselect>
    <input type="text" class="hidden" :name="name" :value="inputValue" />

    <div class="h-5 pl-4" v-if="!noAlert">
      <span v-if="errorMessage" class="text-danger dark:text-danger text-xs">
        {{ $t(errorMessage || "") }}
      </span>
    </div>
  </div>
</template>
<script>
import { ref, computed, toRef } from "vue";
import { useField } from "vee-validate";
import Multiselect from "@vueform/multiselect";

export default {
  name: "MultiSelect",
  components: { Multiselect },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    label: String,
    name: String,
    tooltip: String,
    id: String,
    placeholder: String,
    modelValue: {
      type: String,
      default: "",
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    translate: {
      type: Boolean,
      default: false,
    },
    noAlert: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  // methods: {
  //     changeOption(event) {
  //         this.$emit("update:modelValue", event);
  //     },
  // },
  setup(props, { emit }) {
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      meta,
      checked,
    } = useField(toRef(props, "name"), undefined, {
      initialValue: computed(() => props.modelValue),
    });

    const onChange = (val) => {
      handleChange(val);
      // emit("update:modelValue", val);
    };

    const classess = ref({
      container:
        "relative py-1 text-primary dark:text-gray-300 dark:bg-gray-700 mx-auto w-full flex items-center justify-end box-border cursor-pointer border-2  rounded-2xl  text-base leading-snug outline-hidden",
      containerDisabled: "cursor-default bg-gray-100",
      containerOpen: "rounded-b-none",
      containerOpenTop: "rounded-t-none",
      containerActive: "ring-3 ring-blue-500/30",
      singleLabel:
        "flex items-center h-full max-w-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-4 pr-16 box-border rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5",
      singleLabelText:
        "text-ellipsis overflow-hidden block whitespace-nowrap max-w-full",
      multipleLabel:
        "flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-4 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5 z-10",
      search:
        "w-full absolute inset-0 dark:bg-gray-700 dark:border-gray-600 outline-hidden focus:ring-0 appearance-none box-border border-0 text-base font-sans rounded-2xl h-full pl-4 rtl:pl-0 rtl:pr-3.5",
      tags: "grow shrink flex flex-wrap items-center mt-1 pl-2 rtl:pl-0 rtl:pr-2",
      tag: "bg-blue-500 text-white text-sm font-semibold py-0.5 pl-2 rounded-2xl mr-1 mb-1 flex items-center whitespace-nowrap rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1",
      tagDisabled: "pr-2 opacity-50 rtl:pl-2",
      tagRemove:
        "flex items-center justify-center p-1 mx-0.5 rounded-xs hover:bg-black hover:bg-opacity-10 group",
      tagRemoveIcon:
        "bg-multiselect-remove bg-center bg-no-repeat opacity-30 inline-block w-3 h-3 group-hover:opacity-60",
      tagsSearchWrapper: "inline-block relative mx-1 mb-1 grow shrink h-full",
      tagsSearch:
        "absolute inset-0 border-0 outline-hidden focus:ring-0 appearance-none p-0 text-base font-sans box-border w-full",
      tagsSearchCopy: "invisible whitespace-pre-wrap inline-block h-px",
      placeholder:
        "flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-4 text-gray-400 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5",
      caret:
        "bg-multiselect-caret bg-center bg-no-repeat w-2.5 h-4 py-px box-content mr-3.5 relative z-10 opacity-40 shrink-0 grow-0 transition-transform transform pointer-events-none rtl:mr-0 rtl:ml-3.5",
      caretOpen: "rotate-180 pointer-events-auto",
      clear:
        "pr-3.5 relative z-10 opacity-40 transition duration-300 shrink-0 grow-0 flex hover:opacity-80 rtl:pr-0 rtl:pl-4",
      clearIcon:
        "bg-multiselect-remove bg-center bg-no-repeat w-2.5 h-4 py-px box-content inline-block",
      spinner:
        "bg-multiselect-spinner bg-center bg-no-repeat w-4 h-4 z-10 mr-3.5 animate-spin shrink-0 grow-0 rtl:mr-0 rtl:ml-3.5",
      inifite: "flex items-center justify-center w-full",
      inifiteSpinner:
        "bg-multiselect-spinner bg-center bg-no-repeat w-4 h-4 z-10 animate-spin shrink-0 grow-0 m-3.5",
      dropdown:
        "max-h-60 absolute -left-px -right-px bottom-0 transform translate-y-full border-2 border-gray-300 -mt-px overflow-y-scroll z-50 bg-white dark:bg-gray-700 flex flex-col rounded-b-2xl",
      dropdownTop:
        "-translate-y-full top-px bottom-auto rounded-b-none rounded-t-2xl",
      dropdownHidden: "hidden",
      options: "flex flex-col p-0 m-0 list-none",
      optionsTop: "",
      group: "p-0 m-0",
      groupLabel:
        "flex text-sm box-border items-center justify-start text-left py-1 px-3 font-semibold bg-gray-200 cursor-default leading-normal",
      groupLabelPointable: "cursor-pointer",
      groupLabelPointed: "bg-gray-300 text-gray-700",
      groupLabelSelected: "bg-blue-600 text-white",
      groupLabelDisabled: "bg-gray-100 text-gray-300 cursor-not-allowed",
      groupLabelSelectedPointed: "bg-blue-600 text-white opacity-90",
      groupLabelSelectedDisabled:
        "text-blue-100 bg-blue-600/50 cursor-not-allowed",
      groupOptions: "p-0 m-0",
      option:
        "flex items-center justify-start box-border text-left cursor-pointer text-base leading-snug py-2 px-3",
      optionPointed: "text-gray-800 bg-gray-100",
      optionSelected: "text-white bg-blue-500",
      optionDisabled: "text-gray-300 cursor-not-allowed",
      optionSelectedPointed: "text-white bg-blue-500 opacity-90",
      optionSelectedDisabled: "text-blue-100 bg-blue-500/50 cursor-not-allowed",
      noOptions:
        "py-2 px-3 text-gray-600 bg-white text-left dark:bg-gray-700  ",
      noResults: "py-2 px-3 text-gray-600 bg-white text-left dark:bg-gray-700 ",
      fakeInput:
        "bg-transparent absolute left-0 right-0 -bottom-px w-full h-px border-0 p-0 appearance-none outline-hidden text-transparent",
      spacer: "h-9 py-px box-content",
    });

    return { errorMessage, inputValue, onChange, classess };
  },
};
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss">
// @import "@/assets/select.css";
</style>
